import React, { useState, useEffect } from "react";
import { churnAPI } from "../api";
import { AlertTriangle, User, TrendingDown, Shield, ChevronRight } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from "recharts";
import { motion, AnimatePresence } from "framer-motion";

function ChurnDetailModal({ customer, onClose }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (customer?.id) {
      churnAPI.detail(customer.id).then((res) => {
        setDetail(res.data);
        setLoading(false);
      }).catch(() => setLoading(false));
    }
  }, [customer]);

  if (!customer) return null;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[80vh] overflow-y-auto p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h4 className="text-lg font-bold text-gray-900">Chi tiết nguy cơ rời bỏ</h4>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl">&times;</button>
        </div>

        {loading ? (
          <div className="flex justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                <User className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <p className="font-semibold text-gray-900">{customer.display_name || customer.customer_name}</p>
                <p className="text-sm text-gray-500">ID: {customer.platform_user_id || "N/A"}</p>
              </div>
            </div>

            <div className="bg-red-50 rounded-xl p-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-red-700 font-medium">Mức nguy cơ</span>
                <span className="text-2xl font-bold text-red-600">{detail?.risk_score || customer.risk_score || 0}%</span>
              </div>
              <div className="w-full bg-red-200 rounded-full h-2 mt-2">
                <div
                  className="bg-red-500 h-2 rounded-full transition-all"
                  style={{ width: `${detail?.risk_score || customer.risk_score || 0}%` }}
                />
              </div>
            </div>

            <div>
              <h5 className="text-sm font-semibold text-gray-700 mb-2">Lý do chính</h5>
              <ul className="space-y-2">
                {(detail?.reasons || customer.reasons || ["Sentiment tiêu cực liên tục"]).map((reason, i) => (
                  <li key={i} className="flex items-start space-x-2 text-sm text-gray-600">
                    <AlertTriangle className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
                    <span>{reason}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h5 className="text-sm font-semibold text-gray-700 mb-2">Đề xuất hành động</h5>
              <div className="bg-blue-50 rounded-xl p-3">
                <p className="text-sm text-blue-800">{detail?.recommendation || customer.recommendation || "Liên hệ trực tiếp để giải quyết vấn đề"}</p>
              </div>
            </div>
          </div>
        )}
      </motion.div>
    </div>
  );
}

export default function ChurnPage() {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    fetchChurn();
  }, []);

  const fetchChurn = async () => {
    try {
      setLoading(true);
      const res = await churnAPI.highRisk();
      setCustomers(res.data || []);
    } catch (err) {
      console.error("Churn fetch error:", err);
    } finally {
      setLoading(false);
    }
  };

  const chartData = customers.slice(0, 10).map((c) => ({
    name: c.display_name || c.customer_name || `KH ${c.id}`,
    risk: c.risk_score || 0,
  }));

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-2xl font-bold text-gray-900">Dự đoán Churn</h3>
        <p className="text-gray-500 text-sm mt-1">Khách hàng có nguy cơ rời bỏ cao</p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="card">
          <div className="flex items-center space-x-3">
            <div className="p-2.5 bg-red-100 rounded-xl"><AlertTriangle className="w-5 h-5 text-red-600" /></div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{customers.filter((c) => (c.risk_score || 0) >= 80).length}</p>
              <p className="text-sm text-gray-500">Nguy cơ rất cao (&ge;80%)</p>
            </div>
          </div>
        </div>
        <div className="card">
          <div className="flex items-center space-x-3">
            <div className="p-2.5 bg-amber-100 rounded-xl"><TrendingDown className="w-5 h-5 text-amber-600" /></div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{customers.filter((c) => (c.risk_score || 0) >= 60 && (c.risk_score || 0) < 80).length}</p>
              <p className="text-sm text-gray-500">Nguy cơ cao (60-79%)</p>
            </div>
          </div>
        </div>
        <div className="card">
          <div className="flex items-center space-x-3">
            <div className="p-2.5 bg-blue-100 rounded-xl"><Shield className="w-5 h-5 text-blue-600" /></div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{customers.length}</p>
              <p className="text-sm text-gray-500">Tổng KH theo dõi</p>
            </div>
          </div>
        </div>
      </div>

      {/* Chart */}
      {chartData.length > 0 && (
        <div className="card">
          <h4 className="text-base font-semibold text-gray-900 mb-4">Top 10 khách hàng nguy cơ cao nhất</h4>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={chartData} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis type="number" domain={[0, 100]} tick={{ fontSize: 11 }} />
              <YAxis dataKey="name" type="category" tick={{ fontSize: 11 }} width={120} />
              <Tooltip contentStyle={{ borderRadius: "12px", border: "none", boxShadow: "0 4px 20px rgba(0,0,0,0.1)" }} />
              <Bar dataKey="risk" radius={[0, 6, 6, 0]}>
                {chartData.map((entry, i) => (
                  <Cell key={i} fill={entry.risk >= 80 ? "#ef4444" : entry.risk >= 60 ? "#f59e0b" : "#3b82f6"} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Customer List */}
      <div className="card overflow-x-auto">
        <table className="w-full min-w-[550px]">
          <thead>
            <tr className="border-b border-gray-100">
              <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase">Khách hàng</th>
              <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase">Nguy cơ</th>
              <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase">Lý do chính</th>
              <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase">Hành động</th>
            </tr>
          </thead>
          <tbody>
            {customers.map((c, i) => (
              <tr key={c.id || i} className="border-b border-gray-50 hover:bg-gray-50 transition-colors">
                <td className="py-3 px-4">
                  <p className="text-sm font-medium text-gray-900">{c.display_name || c.customer_name || `KH ${c.id}`}</p>
                </td>
                <td className="py-3 px-4">
                  <span className={`badge ${(c.risk_score || 0) >= 80 ? "badge-negative" : (c.risk_score || 0) >= 60 ? "badge-warning" : "badge-neutral"}`}>
                    {c.risk_score || 0}%
                  </span>
                </td>
                <td className="py-3 px-4 text-sm text-gray-600 max-w-xs truncate">
                  {(c.reasons || []).join(", ") || "N/A"}
                </td>
                <td className="py-3 px-4">
                  <button
                    onClick={() => setSelected(c)}
                    className="text-primary-600 hover:text-primary-700 text-sm font-medium flex items-center"
                  >
                    Chi tiết <ChevronRight className="w-4 h-4 ml-1" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {customers.length === 0 && (
          <div className="text-center py-12">
            <Shield className="w-12 h-12 text-green-300 mx-auto mb-3" />
            <p className="text-gray-500">Không có khách hàng nào có nguy cơ rời bỏ cao</p>
          </div>
        )}
      </div>

      <AnimatePresence>
        {selected && <ChurnDetailModal customer={selected} onClose={() => setSelected(null)} />}
      </AnimatePresence>
    </div>
  );
}
